import React from "react";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockIcon from '@mui/icons-material/Lock';
import { useDispatch, useSelector } from "react-redux";
import { useGenerateDesignMutation, useClaimDesignMutation, useGetProfileQuery } from "../../../services/auth";
import { useCreateAssetMutation } from "../../../services/assets";
import { setCurrentDesign } from "../../../reducers/editorSlice";
import { RootState } from "../../../app/store";
import { toast } from "react-toastify";
import CertificateModal from "../../../Modals/certificateModal";
import Loader from '../../../constants/Loader';

const GenerationSidebar = () => {
  const dispatch = useDispatch();
  const [generateDesign, { isLoading }] = useGenerateDesignMutation();
  const [claimDesign, { isLoading: isClaiming }] = useClaimDesignMutation();
  const [createAsset] = useCreateAssetMutation();
  const [exportingFormat, setExportingFormat] = React.useState<string | null>(null);
  const [isPlacingDesign, setIsPlacingDesign] = React.useState(false);
  const [certificateModalOpen, setCertificateModalOpen] = React.useState(false);
  const [certificateData, setCertificateData] = React.useState<any>(null);
  const { data: profileRes } = useGetProfileQuery();
  const userName = profileRes?.data?.name || profileRes?.data?.first_name || profileRes?.data?.full_name || 'Owner';
  const designInfo = useSelector((state: RootState) => state.editor.currentDesign);
  const isBusy = isLoading || isPlacingDesign || isClaiming;

  React.useEffect(() => {
    const onGenerating = (e: Event) => {
      setIsPlacingDesign(!!(e as CustomEvent).detail?.status);
    };
    window.addEventListener('ai-generating', onGenerating);
    return () => window.removeEventListener('ai-generating', onGenerating);
  }, []);

  const handleClaim = async () => {
    if (isClaiming) return;
    if (!designInfo?.design_id) {
      toast.error("No design selected to claim.");
      return;
    }

    if (designInfo?.is_claimed) {
      setCertificateData({ ...designInfo, owner_name: designInfo.owner_name || userName });
      setCertificateModalOpen(true);
      return;
    }

    try {
      const res = await claimDesign(designInfo.design_id).unwrap();
      const claimData = res?.data?.data || res?.data || res;

      dispatch(setCurrentDesign({ ...designInfo, ...claimData, is_claimed: true }));
      setCertificateData({ ...designInfo, ...claimData });
      toast.success("Design claimed successfully!");
      setCertificateModalOpen(true);
    } catch (err: any) {
      if (err?.status === 409) {
        toast.error("This design has already been claimed.");
        dispatch(setCurrentDesign({ ...designInfo, is_claimed: true }));
      } else if (err?.status === 404) {
        toast.error("Design not found.");
      } else {
        toast.error(err?.data?.message || err?.message || "Failed to claim design");
      }
    }
  };

  const handleRegenerate = async () => {
    try {
      window.dispatchEvent(new CustomEvent('ai-generating', { detail: { status: true } }));
      const payload = {
        title: "Regenerated Design",
        prompt: "Generated from sidebar",
        width: 2048,
        height: 2048,
        seed: "random_seed"
      };
      const res = await generateDesign(payload).unwrap();
      const generatedData = res?.data || res;
      console.log('[Generate] preview_url:', generatedData?.preview_url);

      if (generatedData?.design_id) {
        dispatch(setCurrentDesign(generatedData));
        const event = new CustomEvent('update-main-design', {
          detail: {
            designData: generatedData,
            design_id: generatedData.design_id,
            preview_url: generatedData.preview_url,
            canvas: generatedData.canvas,
            layers: generatedData.layers,
            regions: generatedData.regions,
            animate: true,
            announceSuccess: true,
          }
        });
        window.dispatchEvent(event);

        if (generatedData.preview_url) {
          createAsset({
            asset_type: 'design',
            source_url: generatedData.preview_url,
            mime_type: 'image/png',
            width: generatedData.canvas?.width || 2048,
            height: generatedData.canvas?.height || 2048,
            regions: typeof generatedData.regions === 'string'
              ? generatedData.regions
              : JSON.stringify(generatedData.regions || []),
          }).unwrap()
            .then((assetRes) => {
              const assetData = assetRes?.data || assetRes;
              console.log('[Generate] assets source_url:', assetData?.source_url);
            })
            .catch((assetErr) => {
              console.error('[Generate] assets API failed:', assetErr);
            });
        }
      } else {
        window.dispatchEvent(new CustomEvent('ai-generating', { detail: { status: false } }));
        toast.error("Generated design is missing an id.");
      }
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || "Failed to regenerate design");
      window.dispatchEvent(new CustomEvent('ai-generating', { detail: { status: false } }));
    }
  };

  const handleExport = (format: string) => {
    setExportingFormat(format);
    window.dispatchEvent(new CustomEvent('export-canvas', { detail: { format } }));
  };

  const handlePngClick = () => {
    window.dispatchEvent(new CustomEvent('trigger-upload-image'));
  };

  React.useEffect(() => {
    const onComplete = () => setExportingFormat(null);
    window.addEventListener('export-canvas-complete', onComplete);
    return () => window.removeEventListener('export-canvas-complete', onComplete);
  }, []);

  return (
    <>
      <div className="editor_card">
        <h3>Generation</h3>
        <button
          className="regenerate_btn"
          style={{ marginBottom: '25px', opacity: isBusy ? 0.7 : 1, cursor: isBusy ? 'not-allowed' : 'pointer' }}
          onClick={handleRegenerate}
          disabled={isBusy}
        >
          <AutoAwesomeIcon /> {isLoading ? "Regenerating..." : isPlacingDesign ? "Loading design..." : "Regenerate Design"}
        </button>

        <h3>Design Info</h3>
        <ul className="design_info_list">
          <li>
            <span className="label">Design ID</span>
            <span className="value">{designInfo?.design_id || '-'}</span>
          </li>
          <li>
            <span className="label">Status</span>
            <span className="value">{designInfo?.status || 'Draft'}</span>
          </li>
          <li>
            <span className="label">Created</span>
            <span className="value">{designInfo?.created_at ? new Date(designInfo.created_at).toLocaleString() : '-'}</span>
          </li>
          <li>
            <span className="label">Seed</span>
            <span className="value">{designInfo?.seed || '-'}</span>
          </li>
        </ul>

        <div className="claim_ownership_box" onClick={handleClaim} style={{
          border: '1px solid #4ADE80',
          background: 'rgba(74, 222, 128, 0.1)',
          borderRadius: '8px',
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          marginBottom: '20px',
          cursor: isClaiming ? 'not-allowed' : 'pointer',
          opacity: isClaiming ? 0.6 : 1
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span className="title" style={{ color: '#4ADE80', fontWeight: 'bold', fontSize: '14px' }}>
              {designInfo?.is_claimed ? 'View Certificate' : 'Claim Ownership'}
            </span>
            <span className="subtitle" style={{ color: '#fff', fontSize: '12px' }}>Lock & get certificate</span>
          </div>
          <LockIcon className="lock_icon" style={{ color: '#4ADE80' }} />
        </div>

        <div className="export_btn_group" style={{ display: 'flex', gap: '10px' }}>
          {(['png', 'jpg', 'svg'] as const).map((format) => (
            <button
              key={format}
              className="export_btn"
              onClick={() => {
                if (format === 'png') {
                  handlePngClick();
                } else {
                  handleExport(format);
                }
              }}
              disabled={format !== 'png' ? !!exportingFormat : isBusy}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '15px 0',
                background: '#2c2c2c',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                cursor: exportingFormat ? 'not-allowed' : 'pointer',
                opacity: exportingFormat && exportingFormat !== format ? 0.6 : 1,
              }}
            >
              <img src="/static/images/pdf.svg" alt={format.toUpperCase()} style={{ width: '24px', height: '24px', opacity: 0.7 }} />
              <span style={{ fontSize: '13px' }}>{exportingFormat === format ? '...' : format.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="editor_card">
        <h3>How It Works</h3>

        <div className="how_it_works_list">
          <div className="step_item">
            <div className="icon_circle"><img src="/static/images/step1.svg" alt="Generate" /></div>
            <div className="step_content">
              <h4>Generate</h4>
              <p>Our engine Create a Unique symmetrical design</p>
            </div>
          </div>
          <div className="step_item">
            <div className="icon_circle"><img src="/static/images/step2.svg" alt="Customize" style={{ width: '14px', height: '14px' }} /></div>
            <div className="step_content">
              <h4>Customize</h4>
              <p>Adjust colors, add overlays and text</p>
            </div>
          </div>
          <div className="step_item">
            <div className="icon_circle"><img src="/static/images/step3.svg" alt="Claim" style={{ width: '15px', height: '15px' }} /></div>
            <div className="step_content">
              <h4>Claim Ownership</h4>
              <p>Verify uniqueness and get your certificate</p>
            </div>
          </div>
          <div className="step_item">
            <div className="icon_circle"><img src="/static/images/step4.svg" alt="Export" style={{ width: '14px', height: '14px' }} /></div>
            <div className="step_content">
              <h4>Export</h4>
              <p>Download in PNG, JPG or SVG Formats</p>
            </div>
          </div>
        </div>

        <div className="security_badge">
          <img src="/static/images/shield_icon.svg" alt="Security" className="shield_icon" style={{ width: '24px', height: '24px' }} />
          <p>Every Design you Generate is 100% Unique and Protected.</p>
        </div>
      </div>

      <CertificateModal
        open={certificateModalOpen}
        setOpen={setCertificateModalOpen}
        onClose={() => setCertificateModalOpen(false)}
        claimData={{ ...(certificateData || designInfo), owner_name: certificateData?.owner_name || designInfo?.owner_name || userName }}
      />
      <Loader isLoad={isClaiming} />
    </>
  );
};

export default GenerationSidebar;
